import React, { useEffect, useRef, useState } from 'react';
import { Box, Grid, Paper, Typography, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

interface DashboardChartsProps {
  chartData: any;
  userType: 'client' | 'professional';
  loading?: boolean;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const SafeResponsiveChart: React.FC<{ children: React.ReactNode; height?: number }> = ({ children, height = 320 }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [hasValidSize, setHasValidSize] = useState(false);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const updateSizeState = () => {
      const rect = element.getBoundingClientRect();
      setHasValidSize(rect.width > 0 && rect.height > 0);
    };

    updateSizeState();

    const resizeObserver = new ResizeObserver(() => {
      updateSizeState();
    });
    resizeObserver.observe(element);

    window.addEventListener('resize', updateSizeState);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateSizeState);
    };
  }, []);

  return (
    <Box ref={containerRef} sx={{ width: '100%', height, minWidth: 0, minHeight: height }}>
      {hasValidSize ? (
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      ) : null}
    </Box>
  );
};

const DashboardCharts: React.FC<DashboardChartsProps> = ({ chartData, userType, loading }) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!chartData) {
    return null;
  }

  // Pie Chart Data
  const pieData = userType === 'client' 
    ? [
        { name: t('dashboard.charts.pending'), value: chartData.tasks_by_status?.pending || 0 },
        { name: t('dashboard.charts.in_progress'), value: chartData.tasks_by_status?.in_progress || 0 },
        { name: t('dashboard.charts.completed'), value: chartData.tasks_by_status?.completed || 0 },
        { name: t('dashboard.charts.cancelled'), value: chartData.tasks_by_status?.cancelled || 0 },
      ].filter(item => item.value > 0)
    : [
        { name: t('dashboard.charts.pending'), value: chartData.bids_by_status?.pending || 0 },
        { name: t('dashboard.charts.accepted'), value: chartData.bids_by_status?.accepted || 0 },
        { name: t('dashboard.charts.rejected'), value: chartData.bids_by_status?.rejected || 0 },
      ].filter(item => item.value > 0);

  return (
    <Grid container spacing={3}>
      {/* Pie Chart - Status Distribution */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3, height: 400, borderRadius: 2, boxShadow: '0 4px 15px rgba(0,0,0,0.05)', minWidth: 0 }}>
          <Typography variant="h6" gutterBottom>
            {userType === 'client' 
              ? t('dashboard.charts.tasks_by_status', 'Задачи по статус')
              : t('dashboard.charts.bids_by_status', 'Оферти по статус')
            }
          </Typography>
          <SafeResponsiveChart>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${((percent || 0) * 100).toFixed(0)}%)`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
          </SafeResponsiveChart>
        </Paper>
      </Grid>

      {/* Line Chart - Activity Over Time */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3, height: 400, borderRadius: 2, boxShadow: '0 4px 15px rgba(0,0,0,0.05)', minWidth: 0 }}>
          <Typography variant="h6" gutterBottom>
            {userType === 'client'
              ? t('dashboard.charts.tasks_over_time', 'Задачи през последните 30 дни')
              : t('dashboard.charts.bids_over_time', 'Оферти през последните 30 дни')
            }
          </Typography>
          <SafeResponsiveChart>
              <LineChart
                data={userType === 'client' ? chartData.tasks_over_time : chartData.bids_over_time}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#8884d8" 
                  strokeWidth={2}
                  name={userType === 'client' ? t('dashboard.charts.tasks') : t('dashboard.charts.bids')}
                />
              </LineChart>
          </SafeResponsiveChart>
        </Paper>
      </Grid>

      {/* Bar Chart - Categories (Client) or Revenue (Professional) */}
      <Grid item xs={12}>
        <Paper sx={{ p: 3, height: 400, borderRadius: 2, boxShadow: '0 4px 15px rgba(0,0,0,0.05)', minWidth: 0 }}>
          <Typography variant="h6" gutterBottom>
            {userType === 'client'
              ? t('dashboard.charts.tasks_by_category', 'Задачи по категории')
              : t('dashboard.charts.revenue_over_time', 'Приходи през последните 30 дни')
            }
          </Typography>
          <SafeResponsiveChart>
              {userType === 'client' ? (
                <BarChart
                  data={chartData.tasks_by_category || []}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#8884d8" name={t('dashboard.charts.tasks')} />
                </BarChart>
              ) : (
                <LineChart
                  data={chartData.revenue_over_time || []}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#00C49F" 
                    strokeWidth={2}
                    name={t('dashboard.charts.revenue', 'Приходи (€)')}
                  />
                </LineChart>
              )}
          </SafeResponsiveChart>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default DashboardCharts;
